import { useQuery } from "@tanstack/react-query";
import { cdFetch } from "../api";
import { queryKeys } from "../queryKeys";

export const useReportFilters = (
  userEmail: string | null,
  l1Data: Array<any> | null,
  l2Data: Array<any> | null,
  l3Data: Array<any> | null,
  district: Array<any> | null,
  vendor: Array<any> | null,
  timePeriod: Array<any> | null,
  stock: Array<any> | null,
  purchase: Array<any> | null,
  month: Array<any> | null
) => {
  const { data, isLoading }: any = useQuery({
    queryKey: [
      queryKeys.reportFilters,
      userEmail,
      l1Data,
      l2Data,
      l3Data,
      district,
      vendor,
      timePeriod,
      stock,
      purchase,
      month,
    ],
    queryFn: () =>
      getReportFilters(
        userEmail,
        l1Data,
        l2Data,
        l3Data,
        district,
        vendor,
        timePeriod,
        stock,
        purchase,
        month
      ),
    staleTime: 20000,
  });
  const reportFilters = { ...data?.data?.data?.data };
  return { reportFilters, isLoading };
};

export function getReportFilters(
  userEmail: string | null,
  l1Data: Array<any> | null,
  l2Data: Array<any> | null,
  l3Data: Array<any> | null,
  district: Array<any> | null,
  vendor: Array<any> | null,
  timePeriod: Array<any> | null,
  stock: Array<any> | null,
  purchase: Array<any> | null,
  month: Array<any> | null
) {
  let qParams = "";
  if (l1Data && l1Data.length > 0) {
    const l1Array = `[${l1Data
      .map((item) => `"${item.replace(/&/g, "%26")}"`)
      .join(", ")}]`;
    qParams += `L1=${l1Array}&`;
  }
  if (l2Data && l2Data.length > 0) {
    qParams += `L2=${JSON.stringify(
      l2Data?.map((item) => encodeURIComponent(item))
    )}&`;
  }
  if (l3Data && l3Data.length > 0) {
    qParams += `L3=${JSON.stringify(
      l3Data?.map((item) => encodeURIComponent(item))
    )}&`;
  }
  if (vendor && vendor.length > 0) {
    qParams += `Vendor=${JSON.stringify(
      vendor?.map((item) => encodeURIComponent(item))
    )}&`;
  }

  if (district && district.length > 0) {
    qParams += `District=${JSON.stringify(district)}&`;
  }

  if (stock && stock.length > 0) {
    qParams += `PROJECT_FLAG=${JSON.stringify(stock)}&`;
  }

  if (purchase && purchase.length > 0) {
    qParams += `ATTRIBUTES_VALUES=${JSON.stringify(purchase)}&`;
  }

  if (timePeriod && timePeriod.length > 0) {
    qParams += `Year=${JSON.stringify(timePeriod)}&`;
  }

  if (month && month.length > 0) {
    qParams += `Month=${JSON.stringify(month)}&`;
  }
  if (
    userEmail &&
    userEmail !== "" &&
    userEmail !== null &&
    userEmail !== undefined
  ) {
    qParams += `email=${JSON.stringify(userEmail)}&`;
  }
  // Remove the trailing '&' and prepend '?' if there are any query parameters
  if (qParams.length > 0) {
    qParams = "?" + qParams.slice(0, -1);
  }
  const res = cdFetch(`/getAllFiltersReportsData${qParams}`);
  return res;
}

export const useReportFiltersData = (
  l1Data: Array<any> | null,
  l2Data: Array<any> | null,
  l3Data: Array<any> | null,
  district: Array<any> | null,
  vendor: Array<any> | null,
  timePeriod: Array<any> | null,
  stock: Array<any> | null,
  purchase: Array<any> | null,
  month: Array<any> | null,
  isJPExcluded: number = 1
) => {
  const { data, isLoading, refetch }: any = useQuery({
    queryKey: [
      queryKeys.reports,
      l1Data,
      l2Data,
      l3Data,
      district,
      vendor,
      timePeriod,
      stock,
      purchase,
      month,
      isJPExcluded,
    ],
    queryFn: () =>
      getReportFiltersData(
        l1Data,
        l2Data,
        l3Data,
        district,
        vendor,
        timePeriod,
        stock,
        purchase,
        month,
        isJPExcluded
      ),
    staleTime: 20000,
    enabled: timePeriod !== null,
  });
  let reportData = { ...data?.data?.data?.data };
  let treeData: any[] = [];
  if (reportData?.purchaseSummary && reportData?.purchaseSummary?.length === 0)
    treeData = [];
  else treeData = addParentCommodity(reportData?.purchaseSummary, reportData);
  let totalSpend = treeData ? treeData[0]?.spend : 0;
  let totalVolume = treeData ? treeData[0]?.volume : 0;
  let purchaseOverviewData = reportData?.purchaseOverview;
  let refreshDate = reportData?.refreshDate;
  return {
    reportData,
    treeData,
    totalSpend,
    totalVolume,
    isLoadingReportData: isLoading,
    purchaseOverviewData,
    refreshDate,
    refetch,
  };
};

export function getReportFiltersData(
  l1Data: Array<any> | null,
  l2Data: Array<any> | null,
  l3Data: Array<any> | null,
  district: Array<any> | null,
  vendor: Array<any> | null,
  timePeriod: Array<any> | null,
  stock: Array<any> | null,
  purchase: Array<any> | null,
  month: Array<any> | null,
  isJPExcluded: number = 1
) {
  let qParams = "";
  if (l1Data && l1Data.length > 0) {
    const l1Array = `[${l1Data
      .map((item) => `"${item.replace(/&/g, "%26")}"`)
      .join(", ")}]`;
    qParams += `L1=${l1Array}&`;
  }
  if (l2Data && l2Data.length > 0) {
    qParams += `L2=${JSON.stringify(
      l2Data?.map((item) => encodeURIComponent(item))
    )}&`;
  }
  if (l3Data && l3Data.length > 0) {
    qParams += `L3=${JSON.stringify(
      l3Data?.map((item) => encodeURIComponent(item))
    )}&`;
  }
  if (vendor && vendor.length > 0) {
    qParams += `Vendor=${JSON.stringify(
      vendor?.map((item) => encodeURIComponent(item))
    )}&`;
  }

  if (district && district.length > 0) {
    qParams += `District=${JSON.stringify(district)}&`;
  }

  if (stock && stock.length > 0) {
    qParams += `PROJECT_FLAG=${JSON.stringify(stock)}&`;
  }

  if (purchase && purchase.length > 0) {
    qParams += `ATTRIBUTES_VALUES=${JSON.stringify(purchase)}&`;
  }

  if (timePeriod && timePeriod.length > 0) {
    qParams += `Time=${JSON.stringify(timePeriod)}&`;
  }

  if (month && month.length > 0) {
    qParams += `Month=${JSON.stringify(month)}&`;
  }
  // Remove the trailing '&' and prepend '?' if there are any query parameters
  if (qParams.length > 0) {
    qParams = "?" + qParams.slice(0, -1);
  }
  const res = cdFetch(`/getReportsData${qParams}${qParams ? "&" : "?"}isJPExcluded=${isJPExcluded}`);
  return res;
}

interface Category {
  total_spend: number;
  total_volume: number;
}

interface L3Category extends Category {
  l3_category: string;
}

interface L2Category extends Category {
  l2_category: string;
  l3_categories?: L3Category[];
}

interface L1Category extends Category {
  l1_category: string;
  l2_categories: L2Category[];
}

interface TreeNode {
  key: string;
  name: string;
  spend: number;
  total_spend?: number;
  total_volume?: number;
  volume: number;
  children?: TreeNode[];
}

function convertToTreeTableFormat(
  data: (L1Category | L2Category | L3Category)[],
  level: number = 1,
  parentKey: string = ""
): TreeNode[] {
  return data.map((item, index) => {
    const currentKey = parentKey ? `${parentKey}-${index}` : `${index}`;
    const categoryKey = `l${level}_category` as keyof (
      | L1Category
      | L2Category
      | L3Category
    );
    const subCategoriesKey = `l${level + 1}_categories` as keyof (
      | L1Category
      | L2Category
    );

    const node: TreeNode = {
      key: currentKey,

      name: item[categoryKey] as unknown as string,
      spend: item.total_spend,
      volume: item.total_volume,
    };

    const subCategories = item[subCategoriesKey] as unknown as
      | L2Category[]
      | L3Category[]
      | undefined;
    if (subCategories && subCategories.length > 0) {
      node.children = convertToTreeTableFormat(
        subCategories,
        level + 1,
        currentKey
      );
    }

    return node;
  });
}

function addParentCommodity(data: any, reportData: any): TreeNode[] {
  const totalSpend = reportData?.commodityAllSpend ?? 0;
  const totalVolume = reportData?.commodityAllVolume ?? 0;

  return [
    {
      key: "-1",

      name: "Commodity (All)",
      spend: totalSpend,
      volume: totalVolume,

      children: data && convertToTreeTableFormat(data),
    },
  ];
}
